import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Tooltip,
  TextField,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import InfoIcon from '@mui/icons-material/Info';
import { FormControlLabel, Checkbox } from '@mui/material';
import { EditableDataGrid, createColumns } from '../components/EditableDataGrid';
import { StageProcessor } from '../components/StageProcessor';
import { assignPSCCodes, savePSCAssignment } from '../api/stageFunctions';
import { Product, ProductWithPSC, PSCCode } from '../types/pipeline';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface PSCCodeAssignmentProps {
  products: Product[];
  onComplete: (products: ProductWithPSC[]) => void;
  flowId?: string | null;
}


const PSCCodeAssignment: React.FC<PSCCodeAssignmentProps> = ({
  products,
  onComplete,
  flowId,
}) => {
  const [productsWithPSC, setProductsWithPSC] = useState<ProductWithPSC[]>([]);
  const [triggerAssignment, setTriggerAssignment] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [useCache, setUseCache] = useState(true);
  
  // Build normalized input mirroring backend to ensure status matches backend decisions
  // IMPORTANT: For cache validation, mirror Stage 4 backend input at time of save (pre-PSC/NAICS)
  // Do NOT include psc_codes or naics_from_psc here, as they are outputs of later stages
  const normalizedProducts = useMemo(() => (products || []).map((p: any) => ({
    id: p.id,
    product_name: p.product_name || p.name,
    manufacturer: p.manufacturer,
    model: p.model,
    specifications: {
      award_id: p.specifications?.award_id,
      piid: p.specifications?.piid,
      award_amount: p.specifications?.award_amount,
    },
    evidence: {
      source: p.evidence?.source,
      url: p.evidence?.url,
      date: p.evidence?.date,
    },
  })), [products]);

  // Check cache for this stage (use normalized input so UI status matches backend)
  const { cachedData, isCacheValid, isCheckingCache, validateCacheWithInput } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.PSC,
    inputData: { products: normalizedProducts },
    enabled: true,
  });

  // Validate cache with current products once per mount
  useEffect(() => {
    if (flowId && !hasInitialized) {
      validateCacheWithInput();
    }
  }, [flowId, hasInitialized, validateCacheWithInput]);

  // Default checkbox to match cache availability (checked when cache valid, unchecked when not)
  useEffect(() => {
    if (!isCheckingCache) {
      setUseCache(isCacheValid);
    }
  }, [isCheckingCache, isCacheValid]);

  // Auto-assign on mount - always trigger at least once
  useEffect(() => {
    // Wait for cache check to complete
    if (!isCheckingCache && !hasInitialized) {
      setHasInitialized(true);
      // Always trigger at least once to let StageProcessor handle cached or fresh data
      setTriggerAssignment(1);
    }
  }, [isCheckingCache, hasInitialized]);
  
  // Load cached data when available
  useEffect(() => {
    if (isCacheValid && cachedData && productsWithPSC.length === 0) {
      handlePSCAssigned(cachedData);
    }
  }, [isCacheValid, cachedData, productsWithPSC.length]);

  const handleAssignPSC = () => {
    setTriggerAssignment(prev => prev + 1);
  };

  const handlePSCAssigned = (data: any) => {
    // Accept multiple shapes: {products_with_psc: [...]}, {products: [...]}, or direct array
    const raw = data?.products_with_psc || data?.products || data;

    if (!Array.isArray(raw)) {
      console.error('PSCCodeAssignment: Expected products array but got:', data);
      setProductsWithPSC([]);
      return;
    }

    // Ensure each row has an id; derive from name if missing
    const normalized = raw.map((p: any, idx: number) => {
      let id = p.id;
      if (!id) {
        const base = (p.product_name || p.name || 'product').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        id = `${base}-${idx}`;
      }
      return { id, ...p };
    });

    setProductsWithPSC(normalized);
  };


  const handleComplete = async () => {
    // Persist edited PSCs to backend cache before proceeding
    if (flowId && productsWithPSC.length > 0) {
      try {
        await savePSCAssignment(productsWithPSC, flowId);
      } catch (e) {
        console.warn('Failed to save PSC edits; continuing anyway', e);
      }
    }
    onComplete(productsWithPSC);
  };

  // Define columns for PSC assignment grid
  const pscColumns = [
    {
      field: 'name',
      headerName: 'Product Name',
      width: 200,
      valueGetter: (params: any) => {
        // Use product_name first, fallback to name
        return params.row.product_name || params.row.name || 'Unknown Product';
      },
    },
    createColumns.text('manufacturer', 'Manufacturer', 150),
    createColumns.text('model', 'Model', 120),
    {
      field: 'pscCode',
      headerName: 'Primary PSC',
      width: 140,
      editable: true,
      valueGetter: (params: any) => {
        // Get from psc_codes array (objects or strings) or legacy field
        const primaryPsc = params.row.psc_codes?.[0] || params.row.pscCode;
        if (typeof primaryPsc === 'object' && primaryPsc !== null && 'code' in primaryPsc) {
          return primaryPsc.code;
        }
        return primaryPsc || '';
      },
      valueSetter: (params: any) => {
        // Write back edited PSC code into row.psc_codes[0]
        const newCodeRaw = (params.value || '').toString();
        const newCode = newCodeRaw.includes(':') ? newCodeRaw.split(':')[0].trim() : newCodeRaw.trim();
        const row = { ...params.row };
        const psc0 = row.psc_codes?.[0];
        if (Array.isArray(row.psc_codes) && row.psc_codes.length > 0) {
          if (typeof psc0 === 'object' && psc0 !== null) {
            row.psc_codes = [{ ...psc0, code: newCode }, ...row.psc_codes.slice(1)];
          } else {
            row.psc_codes = [newCode, ...row.psc_codes.slice(1)];
          }
        } else {
          row.psc_codes = [newCode];
        }
        row.pscCode = newCode;
        return row;
      },
      renderCell: (params: any) => {
        // Primary PSC chip without hover tooltip (description is shown in a separate column)
        const pscData = params.row.psc_codes?.[0] || params.value;
        if (!pscData) return null;

        const isObject = typeof pscData === 'object' && pscData !== null && 'code' in pscData;
        let code = isObject ? String((pscData as any).code) : String(pscData);

        // If code includes a description (e.g., "4240: Safety..."), strip the text after ':'
        if (code.includes(':')) {
          const parts = code.split(':');
          code = parts[0].trim();
        }

        return (
          <Chip 
            label={code}
            size="small" 
            color="primary"
            variant="outlined"
          />
        );
      },
      renderEditCell: (params: any) => {
        // Simple text editor for the PSC code
        const { id, field, value, api } = params;
        return (
          <TextField
            size="small"
            autoFocus
            value={value || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              api.setEditCellValue({ id, field, value: e.target.value }, e);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                api.stopCellEditMode({ id, field });
              }
            }}
          />
        );
      },
    },
    {
      field: 'pscDescription',
      headerName: 'PSC Description',
      width: 350,  // Increased from 250 to give more space
      valueGetter: (params: any) => {
        // Get PSC description from backend data
        // Check if psc_codes contains objects with descriptions
        const primaryPsc = params.row.psc_codes?.[0];
        if (primaryPsc) {
          if (typeof primaryPsc === 'object') {
            // First check if description is in the description field
            if (primaryPsc.description && primaryPsc.description.trim()) {
              return primaryPsc.description;
            }
            // If description field is empty, check if it's embedded in the code field
            else if (primaryPsc.code && typeof primaryPsc.code === 'string' && primaryPsc.code.includes(':')) {
              const parts = primaryPsc.code.split(':');
              return parts.slice(1).join(':').trim();
            }
          } else if (typeof primaryPsc === 'string' && primaryPsc.includes(':')) {
            // Extract description from "4240: Safety..." format
            const parts = primaryPsc.split(':');
            return parts.slice(1).join(':').trim();
          }
        }
        // Fallback to psc_descriptions field if it exists
        if (params.row.psc_descriptions?.length > 0) {
          return params.row.psc_descriptions[0];
        }
        // Otherwise return empty string
        return '';
      },
    },
    {
      field: 'alternativePSCs',
      headerName: 'Additional PSCs',
      width: 200,
      valueGetter: (params: any) => {
        // Get additional PSC codes from psc_codes array (skip first)
        const additionalPSCs = params.row.psc_codes?.slice(1) || params.row.alternativePSCs || [];
        // Convert to string representation for editing
        return additionalPSCs.map((psc: any) => {
          if (typeof psc === 'object' && psc !== null) {
            return psc.code || String(psc);
          }
          return String(psc);
        }).join(', ');
      },
      renderCell: (params: any) => {
        // Get additional PSCs from psc_codes array (skip first which is primary)
        const additionalPSCs = params.row.psc_codes?.slice(1) || params.row.alternativePSCs || [];
        if (!additionalPSCs || additionalPSCs.length === 0) {
          return <Typography variant="body2" color="text.secondary">None</Typography>;
        }
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {additionalPSCs.slice(0, 2).map((pscCode: string | PSCCode, idx: number) => {
              const isObject = typeof pscCode === 'object' && pscCode !== null;
              let code = isObject ? (pscCode as PSCCode).code : String(pscCode);
              let description = isObject ? (pscCode as PSCCode).description : '';
              
              // Handle case where code contains description (e.g., "4240: Safety...")
              if (code.includes(':')) {
                const parts = code.split(':');
                code = parts[0].trim();
                if (!description && parts[1]) {
                  description = parts.slice(1).join(':').trim();
                }
              }
              
              return (
                <Tooltip 
                  key={idx}
                  title={description || `PSC Code: ${code}`}
                  placement="top"
                  arrow
                >
                  <Chip 
                    label={code}
                    size="small" 
                    variant="outlined"
                    color="primary"
                    sx={{ cursor: 'help' }}
                  />
                </Tooltip>
              );
            })}
            {additionalPSCs.length > 2 && (
              <Tooltip
                title={additionalPSCs.slice(2).map((psc: any) => {
                  const isObject = typeof psc === 'object' && psc !== null;
                  let code = isObject ? psc.code : String(psc);
                  let desc = isObject ? psc.description : '';
                  
                  // Handle case where code contains description (e.g., "4240: Safety...")
                  if (code.includes(':')) {
                    const parts = code.split(':');
                    code = parts[0].trim();
                    if (!desc && parts[1]) {
                      desc = parts.slice(1).join(':').trim();
                    }
                  }
                  
                  return desc ? `${code}: ${desc}` : `PSC ${code}`;
                }).join('\n')}
                placement="top"
                arrow
              >
                <Chip 
                  label={`+${additionalPSCs.length - 2}`} 
                  size="small" 
                  variant="outlined"
                  sx={{ cursor: 'help' }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  // Calculate statistics
  const stats = {
    total: productsWithPSC.length,
    withCodes: productsWithPSC.filter(p => p.psc_codes && p.psc_codes.length > 0).length,
    uniquePSCs: new Set(
      productsWithPSC.flatMap(p => {
        const codes = p.psc_codes || [];
        // psc_codes is already an array of strings
        return codes;
      })
    ).size,
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          PSC Code Assignment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Product Service Codes (PSC) are used to describe products, services, and research and 
          development purchased by the federal government. Review and adjust the assigned codes as needed.
        </Typography>
      </Paper>

      {/* Cache Control and Assignment Button */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCache && isCacheValid}
                onChange={(e) => setUseCache(e.target.checked)}
                color="primary"
                disabled={isCheckingCache || !isCacheValid}
              />
            }
            label="Use cached data when available"
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CategoryIcon />}
            onClick={() => setTriggerAssignment(prev => prev + 1)}
            disabled={isCheckingCache}
          >
            {getButtonText('Assign PSC Codes', useCache, cachedData && (cachedData.products_with_psc || cachedData))}
          </Button>
        </Box>
      </Paper>
      
      <StageProcessor
        title="Assigning PSC Codes"
        description="Analyzing products to assign appropriate PSC codes..."
        processFunction={() => assignPSCCodes(products, flowId, !useCache)}
        onSuccess={handlePSCAssigned}
        trigger={triggerAssignment}
        autoProcess={false}
        showStatus={true}
      >
        {(data, status) => {
          if (!data || productsWithPSC.length === 0) return null;

          return (
            <>
              {/* Statistics */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" gap={2} alignItems="center">
                  <Chip 
                    label={`${stats.total} Products Coded`} 
                    color="primary" 
                  />
                  <Chip 
                    label={`${stats.uniquePSCs} Unique PSCs`} 
                    color="info"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${stats.withCodes} Products with Codes`} 
                    color="success"
                    variant="outlined"
                  />
                </Box>
              </Paper>

              {/* Info Alert */}
              <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                Double-click on the PSC code to edit it.
              </Alert>

              {/* PSC Assignment Grid */}
              <Paper sx={{ p: 2 }}>
                <EditableDataGrid
                  data={productsWithPSC}
                  columns={pscColumns}
                  onDataChange={setProductsWithPSC}
                  onAddRow={() => {
                    const newProduct: any = {
                      id: `custom-${Date.now()}`,
                      name: 'New Product',
                      manufacturer: 'Custom',
                      model: '',
                      specifications: [],
                      price: '0',
                      gsaSchedule: false,
                      contractVehicle: 'Open Market',
                      leadTime: 30,
                      pscCode: '7010',
                      pscDescription: 'ADP Central Processing Unit',
                      alternativePSCs: [],
                      confidence: 50,
                    };
                    return newProduct;
                  }}
                  height={500}
                  showToolbar={true}
                  selectable={false}
                  customActions={
                    <Button
                      variant="outlined"
                      startIcon={<CategoryIcon />}
                      onClick={handleAssignPSC}
                    >
                      Re-assign PSCs
                    </Button>
                  }
                />

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    PSC codes will be used to identify relevant NAICS codes
                  </Typography>
                  <Box>
                    {/* JSON export removed for non-technical users */}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleComplete}
                    >
                      Continue to NAICS Mapping
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          );
        }}
      </StageProcessor>
    </Box>
  );
};

export default PSCCodeAssignment;
